import {
  Card,
  type CardProps,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { ReactElement } from "react";

import { IconText } from "../../icon-text/IconText";

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "disableElevationAnimation",
})<{ disableElevationAnimation: boolean }>(
  ({ theme, disableElevationAnimation }) => ({
    border: disableElevationAnimation
      ? `1px solid ${theme.palette.primary.L70}`
      : `1px solid ${theme.palette.support[300]}`,
    transition: disableElevationAnimation ? "none" : "all 0.2s ease-out",
    flex: 1,
    "&:hover": disableElevationAnimation
      ? {}
      : {
          transform: "translateY(-4px)",
          boxShadow: theme.shadows[5],
        },
  }),
);

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { MessageCardProps } from '@monthly/ui' */
export interface MessageCardProps extends CardProps {
  title?: string;
  icon: ReactElement;
  emoji?: string;
  value?: string;
  valueSuffix?: string;
  subtitle: string;
  description: string;
  useDivider?: boolean;
  /**
   * Indicates whether to disable the elevation effect on hover.
   * @default false
   */
  disableElevationAnimation?: boolean;
}

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { MessageCard } from '@monthly/ui' */
export function MessageCard({
  title,
  icon,
  emoji,
  value,
  valueSuffix,
  subtitle,
  description,
  disableElevationAnimation = false,
  ...props
}: MessageCardProps) {
  return (
    <StyledCard
      elevation={disableElevationAnimation ? 0 : 2}
      disableElevationAnimation={disableElevationAnimation}
      {...props}
    >
      <Stack padding="16px 12px" gap={2}>
        {title && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              variant="body1"
              color="secondary.dark"
              sx={(theme) => ({
                fontWeight: theme.typography.fontWeightSemiBold,
              })}
            >
              {title}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.5}>
              {emoji}
              <Typography variant="h2" color="primary.main">
                {value}
                <Typography
                  component={"span"}
                  variant="body1"
                  sx={(theme) => ({
                    color: theme.palette.secondary.main,
                  })}
                >
                  {valueSuffix}
                </Typography>
              </Typography>
            </Stack>
          </Stack>
        )}

        {title && (
          <Divider
            sx={(theme) => ({
              borderColor: theme.palette.primary.main,
            })}
          />
        )}
        {icon ? (
          <IconText icon={icon}>{subtitle}</IconText>
        ) : (
          subtitle && (
            <Typography
              variant="body2"
              color="primary.main"
              sx={(theme) => ({
                fontWeight: theme.typography.fontWeightSemiBold,
              })}
            >
              {subtitle}
            </Typography>
          )
        )}
        <Typography variant="body2" color="text.primary">
          {description}
        </Typography>
      </Stack>
    </StyledCard>
  );
}
